import GeoJSON from 'ol/format/GeoJSON';
import * as olExtent from 'ol/extent';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
import { useLayers } from '@agissc/ktag-map';
import useAppState from '../../AppStore';
import { useAttributesQuery } from '../mapContent/topics/attributeSearch/useAttributeQuery';
import { useMetadataQuery } from '../mapContent/api/useMetadataQuery';

export const useAttributeSearchData = (topicId: string | null) => {
  const attributeSearch = useAppState((state) => state.attributeSearch);

  const { sublayerId, query } = topicId ? (attributeSearch[topicId] ?? {}) : {};

  const layersQuery = useLayers();
  const topicLayers = layersQuery.data ?? {};
  const topicLayer = topicId ? topicLayers[topicId] : undefined;
  const { isPublic = true, url: baseUrl } = topicLayer ?? {};

  const url =
    sublayerId !== undefined && query
      ? `${baseUrl}/${sublayerId}/query?where=${encodeURIComponent(query ?? '')}`
      : undefined;

  const attributesQuery = useAttributesQuery(url, isPublic);

  return attributesQuery.data || [];
};

export const useAttributeSearch = (topicId: string) => {
  const setLocation = useAppState((state) => state.setLocation);
  const attributeSearch = useAppState((state) => state.attributeSearch);
  const setAttributeSearch = useAppState((state) => state.setAttributeSearch);

  const { sublayerId, query, selectedFeatureId, highlightedFeatureId } =
    attributeSearch[topicId];

  const layersQuery = useLayers();
  const topicLayers = layersQuery.data ?? {};
  const topicLayer = topicLayers[topicId];
  const { isPublic = true, url: baseUrl } = topicLayer ?? {};

  const url = `${baseUrl}/${sublayerId}/query?where=${encodeURIComponent(query ?? '')}`;
  const attributesQuery = useAttributesQuery(url, isPublic);
  const metadataQuery = useMetadataQuery(topicId);

  const zoomToFeatures = () => {
    if (!attributesQuery.data) return;

    const geojsonFeatures = new GeoJSON().readFeatures(attributesQuery.data);
    const extent = olExtent.createEmpty();

    geojsonFeatures.forEach((feature: Feature<Geometry>) => {
      const featureExtent = feature.getGeometry()?.getExtent();
      if (featureExtent) {
        olExtent.extend(extent, featureExtent);
      }
    });

    setLocation({
      extent: {
        xMin: extent[0],
        yMin: extent[1],
        xMax: extent[2],
        yMax: extent[3],
      },
    });
  };

  const zoomToFeature = (feature: any) => {
    const geojsonFeature = new GeoJSON().readFeatures(feature);
    const geom = geojsonFeature[0].getGeometry();

    if (geom?.getType() === 'Point') {
      setLocation({
        zoom: 10,
        center: {
          x: feature.geometry?.coordinates[0],
          y: feature.geometry?.coordinates[1],
        },
      });
    } else {
      const extent = geom?.getExtent();
      if (extent) {
        setLocation({
          extent: {
            xMin: extent[0],
            yMin: extent[1],
            xMax: extent[2],
            yMax: extent[3],
          },
        });
      }
    }
  };

  const setSelectedFeature = (feature: any) => {
    const featureId = selectedFeatureId === feature.id ? undefined : feature.id;

    setAttributeSearch(topicId, {
      ...attributeSearch[topicId],
      selectedFeatureId: featureId,
    });

    if (featureId) {
      zoomToFeature(feature);
    }
  };

  const setHighlightedFeature = (featureId?: number) => {
    if (highlightedFeatureId === featureId) return;

    setAttributeSearch(topicId, {
      ...attributeSearch[topicId],
      highlightedFeatureId: featureId,
    });
  };

  return {
    attributesQuery,
    metadataQuery,
    sublayerId,
    selectedFeatureId,
    setSelectedFeature,
    highlightedFeatureId,
    setHighlightedFeature,
    zoomToFeatures,
  };
};
